/**
 * EnvironmentPreferencesMatrix - Grid-based environment preference selector
 *
 * A matrix UI where:
 * - Columns = Days of the week (Mon-Sun)
 * - Rows = Environment options (Indoor, Outdoor, Both)
 * - Single click to assign an environment preference to a day
 * - Rest days are automatically disabled
 */

import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import type {
  DayOfWeek,
  EnvironmentPreferenceType,
  UserProfile,
  WeeklyEnvironmentPreferences,
} from '@shared/types';
import React from 'react';

interface EnvironmentPreferencesMatrixProps {
  profile: UserProfile;
  onChange: (updatedProfile: Partial<UserProfile>) => void;
}

const DAYS_OF_WEEK: { full: DayOfWeek; short: string }[] = [
  { full: 'Monday', short: 'Mon' },
  { full: 'Tuesday', short: 'Tue' },
  { full: 'Wednesday', short: 'Wed' },
  { full: 'Thursday', short: 'Thu' },
  { full: 'Friday', short: 'Fri' },
  { full: 'Saturday', short: 'Sat' },
  { full: 'Sunday', short: 'Sun' },
];

interface EnvironmentTypeConfig {
  value: EnvironmentPreferenceType;
  label: string;
  shortLabel: string;
  icon: string;
  color: string;
}

const ENVIRONMENT_TYPES: EnvironmentTypeConfig[] = [
  { value: 'indoor', label: 'Indoor', shortLabel: 'In', icon: '🏠', color: '#0288d1' },
  { value: 'outdoor', label: 'Outdoor', shortLabel: 'Out', icon: '🌳', color: '#388e3c' },
  { value: 'both', label: 'Both OK', shortLabel: 'Both', icon: '✓', color: '#7b1fa2' },
];

const EnvironmentPreferencesMatrix: React.FC<EnvironmentPreferencesMatrixProps> = ({
  profile,
  onChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const envPreferences =
    profile.weeklyEnvironmentPreferences || ({} as WeeklyEnvironmentPreferences);
  const workoutPreferences = profile.weeklyWorkoutPreferences || {};

  // Check if a day is a rest day
  const isRestDay = (day: DayOfWeek): boolean => {
    const prefs = workoutPreferences[day];
    if (Array.isArray(prefs)) {
      return prefs.length === 1 && prefs[0] === 'rest';
    }
    return prefs === 'rest';
  };

  const handleCellClick = (day: DayOfWeek, envType: EnvironmentPreferenceType) => {
    // Don't allow changes on rest days
    if (isRestDay(day)) return;

    const updatedPreferences: WeeklyEnvironmentPreferences = {
      ...envPreferences,
      [day]: envType,
    };

    onChange({ weeklyEnvironmentPreferences: updatedPreferences });
  };

  // Get the current environment preference for a day
  const getEnvPref = (day: DayOfWeek): EnvironmentPreferenceType | undefined => {
    if (isRestDay(day)) return undefined;
    return envPreferences[day] || 'both'; // Default to 'both' if not set
  };

  return (
    <Box>
      {/* Matrix Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `auto repeat(7, 1fr)`,
          gap: 0.5,
          fontSize: isMobile ? '0.65rem' : '0.75rem',
        }}
      >
        {/* Header row - Days */}
        <Box /> {/* Empty corner cell */}
        {DAYS_OF_WEEK.map(day => (
          <Box
            key={day.full}
            sx={{
              textAlign: 'center',
              fontWeight: 600,
              py: 0.5,
              color: isRestDay(day.full) ? 'text.disabled' : 'text.secondary',
            }}
          >
            {isMobile ? day.short.charAt(0) : day.short}
          </Box>
        ))}
        {/* Environment type rows */}
        {ENVIRONMENT_TYPES.map(envType => (
          <React.Fragment key={envType.value}>
            {/* Row label */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                pr: 1,
                whiteSpace: 'nowrap',
              }}
            >
              <span>{envType.icon}</span>
              {!isMobile && (
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  {envType.shortLabel}
                </Typography>
              )}
            </Box>

            {/* Day cells */}
            {DAYS_OF_WEEK.map(day => {
              const isRest = isRestDay(day.full);
              const currentPref = getEnvPref(day.full);
              const isSelected = !isRest && currentPref === envType.value;

              return (
                <Box
                  key={`${day.full}-${envType.value}`}
                  onClick={() => handleCellClick(day.full, envType.value)}
                  aria-label={`Set ${day.full} environment to ${envType.label}`}
                  role="button"
                  tabIndex={isRest ? -1 : 0}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: isMobile ? 28 : 32,
                    borderRadius: 1,
                    cursor: isRest ? 'not-allowed' : 'pointer',
                    transition: 'all 0.15s ease',
                    border: '1px solid',
                    borderColor: isRest
                      ? 'action.disabledBackground'
                      : isSelected
                        ? envType.color
                        : 'divider',
                    bgcolor: isRest
                      ? 'action.disabledBackground'
                      : isSelected
                        ? `${envType.color}20`
                        : 'background.paper',
                    opacity: isRest ? 0.4 : 1,
                    '&:hover': isRest
                      ? {}
                      : {
                          bgcolor: isSelected ? `${envType.color}30` : 'action.hover',
                          transform: 'scale(1.05)',
                        },
                  }}
                >
                  {isSelected && (
                    <Typography
                      sx={{
                        fontSize: isMobile ? '0.9rem' : '1rem',
                      }}
                    >
                      {envType.icon}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

export default EnvironmentPreferencesMatrix;
